import type {
  ClinicianWorkbenchPatientRecordFieldPath,
  ClinicianWorkbenchPatientRecordV1,
} from "@amplecare/contracts";

import { getPatientRecordValueByPath } from "./record-core.js";

export type PatientRecordSectionId =
  | "patient"
  | "case"
  | "contacts"
  | "insurance";

export interface PatientRecordSectionDefinition {
  id: PatientRecordSectionId;
  titleText: string;
  fieldPaths: ClinicianWorkbenchPatientRecordFieldPath[];
}

export const patientRecordSectionDefinitions: PatientRecordSectionDefinition[] =
  [
    {
      id: "patient",
      titleText: "Patient & contact",
      fieldPaths: [
        "patient.firstName",
        "patient.lastName",
        "patient.mrn",
        "patient.dateOfBirth",
        "patient.sex",
        "patient.addressStreet",
        "patient.addressCity",
        "patient.addressState",
        "patient.addressZip",
        "patient.heightInches",
        "patient.weightPounds",
        "patient.primaryPhone.number",
        "patient.email",
        "patient.primaryLanguage",
      ],
    },
    {
      id: "case",
      titleText: "Case",
      fieldPaths: [
        "case.dateOfService",
        "case.caseTime",
        "case.surgeonName",
        "case.procedures",
        "case.surgeryDurationMinutes",
        "case.surgeryTypeCategory",
      ],
    },
    {
      id: "contacts",
      titleText: "Contacts & care team",
      fieldPaths: [
        "contacts.rideHome.name",
        "contacts.rideHome.phone",
        "contacts.emergencyContact1.name",
        "contacts.emergencyContact1.phone",
        "contacts.primaryCarePhysician.name",
      ],
    },
    {
      id: "insurance",
      titleText: "Insurance & benefits",
      fieldPaths: [
        "insurance.primaryPayer.companyName",
        "insurance.primaryPayer.policyNumber",
        "insurance.primaryPayer.groupNumber",
        "insurance.primaryPayer.policyHolderName",
        "insurance.secondaryPayerStatus",
        "insurance.benefits.deductibleCents",
        "insurance.benefits.deductibleMetCents",
        "insurance.benefits.outOfPocketMaxCents",
        "insurance.benefits.outOfPocketMetCents",
        "insurance.benefits.coInsurancePercent",
        "insurance.benefits.coPaymentCents",
        "insurance.benefits.patientResponsibilityCents",
      ],
    },
  ];

export function isPatientRecordFieldFilled(
  record: ClinicianWorkbenchPatientRecordV1,
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath,
): boolean {
  const value = getPatientRecordValueByPath(record, fieldPath);

  if (value == null) {
    return false;
  }

  if (typeof value === "string") {
    if (fieldPath === "insurance.secondaryPayerStatus") {
      return value !== "unknown";
    }

    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
}

export interface SectionReadiness {
  id: PatientRecordSectionId;
  titleText: string;
  filledCount: number;
  totalCount: number;
  missingFieldPaths: ClinicianWorkbenchPatientRecordFieldPath[];
}

function resolveSectionFieldPaths(
  definition: PatientRecordSectionDefinition,
  record: ClinicianWorkbenchPatientRecordV1,
): ClinicianWorkbenchPatientRecordFieldPath[] {
  if (definition.id !== "insurance") {
    return definition.fieldPaths;
  }

  // A confirmed absence of secondary coverage removes those fields from the
  // denominator; "unknown" keeps only the status question itself in play.
  if (record.insurance.secondaryPayerStatus === "present") {
    return [
      ...definition.fieldPaths,
      "insurance.secondaryPayer.companyName",
      "insurance.secondaryPayer.policyNumber",
      "insurance.secondaryPayer.groupNumber",
    ];
  }

  return definition.fieldPaths;
}

export function computePatientRecordReadiness(
  record: ClinicianWorkbenchPatientRecordV1 | null,
): SectionReadiness[] {
  return patientRecordSectionDefinitions.map((definition) => {
    if (!record) {
      return {
        id: definition.id,
        titleText: definition.titleText,
        filledCount: 0,
        totalCount: definition.fieldPaths.length,
        missingFieldPaths: [...definition.fieldPaths],
      };
    }

    const fieldPaths = resolveSectionFieldPaths(definition, record);
    const missingFieldPaths = fieldPaths.filter(
      (fieldPath) => !isPatientRecordFieldFilled(record, fieldPath),
    );

    return {
      id: definition.id,
      titleText: definition.titleText,
      filledCount: fieldPaths.length - missingFieldPaths.length,
      totalCount: fieldPaths.length,
      missingFieldPaths,
    };
  });
}
